import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';
import { getPdfMapByName, getPdfTypeToServerEnum } from '../utils/pdfTypeMaps';

/**
 * Request model for PDF generation
 */
export interface PdfTestRequest {
    title?: string;
    subtitle?: string;
    fileName?: string;
    items?: Array<{
        name: string;
        quantity: number;
        price: number;
    }>;
    sections?: string[];
}

/**
 * Service for generating PDF documents
 */
export class PdfApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    /**
     * Generates a PDF document with test content
     * @param request - Optional request parameters for customizing the PDF
     * @returns Promise containing the PDF blob
     */
    async generateTestPdf(request?: PdfTestRequest): Promise<Blob> {
        const response = await this.postJson('/v1/test/pdf/generate', request || {});

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
                // If we can't parse the error, use the default message
            }
            throw new Error(errorMessage);
        }

        return response.blob();
    }

    /**
     * Generates a PDF for a case
     * @param caseId - The case ID to generate the PDF for
     * @param pdfType - The type of PDF to generate (e.g., "summary")
     * @param parameters - Optional dictionary of parameters for PDF generation (e.g., { affidavitDate: "2025-01-15", affidavitIsBlank: false })
     * @returns Promise containing the PDF blob and filename
     */
    async generateCasePdf(caseId: string, pdfType: string, parameters?: Record<string, string | boolean | number | string[] | null>): Promise<{ blob: Blob; fileName: string }> {
        const requestBody: any = {
            caseId,
            pdfType
        };
        
        // Include parameters dictionary if provided
        if (parameters && Object.keys(parameters).length > 0) {
            requestBody.parameters = parameters;
        }
        
        const response = await this.postJson('/v1/pdf/generate', requestBody);

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            let errorDetails: any = null;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
                errorDetails = errorData;

                // Build a more detailed error message if diagnostic info is available
                if (errorData.taxYear || errorData.stateCode || errorData.pdfType) {
                    const details = [];
                    if (errorData.taxYear) details.push(`Tax Year: ${errorData.taxYear}`);
                    if (errorData.stateCode) details.push(`State: ${errorData.stateCode}`);
                    if (errorData.pdfType) details.push(`PDF Type: ${errorData.pdfType}`);
                    if (details.length > 0) {
                        errorMessage += ` (${details.join(', ')})`;
                    }
                }
            } catch {
                // If we can't parse the error, use the default message
            }
            const error = new Error(errorMessage);
            (error as any).details = errorDetails;
            throw error;
        }

        const blob = await response.blob();

        // Extract filename from Content-Disposition header
        let fileName = `case-${caseId}-${pdfType}.pdf`; // Fallback filename

        // Log all headers for debugging
        console.log('Response headers:', Array.from(response.headers.entries()));

        const contentDisposition = response.headers.get('Content-Disposition');
        console.log('Content-Disposition header:', contentDisposition);

        if (contentDisposition) {
            // Try multiple patterns to extract filename
            // Pattern 1: filename="value" or filename='value'
            let fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

            // Pattern 2: filename*=UTF-8''value (RFC 5987 encoding)
            if (!fileNameMatch) {
                fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            }

            // Pattern 3: filename*=value
            if (!fileNameMatch) {
                fileNameMatch = contentDisposition.match(/filename\*=([^;]+)/);
            }

            if (fileNameMatch && fileNameMatch[1]) {
                // Remove quotes if present and trim whitespace
                fileName = fileNameMatch[1].replace(/^['"]|['"]$/g, '').trim();
                // Decode URI encoding if present (for RFC 5987)
                try {
                    fileName = decodeURIComponent(fileName);
                } catch (e) {
                    // If decoding fails, use the original value
                }
                console.log('Extracted filename:', fileName);
            } else {
                console.warn('Could not extract filename from Content-Disposition:', contentDisposition);
            }
        } else {
            console.warn('Content-Disposition header not found in response');
        }

        return { blob, fileName };
    }

    /**
     * Generates Calculon (AI) allocation advice PDF for a case.
     */
    async generateCalculonAllocationAdvicePdf(
        caseId: string,
        includeLineItems: boolean = false
    ): Promise<{ blob: Blob; fileName: string }> {
        const response = await this.postJson('/v1/pdf/calculon-allocation-analysis', {
            caseId,
            includeLineItems,
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
                // ignore
            }
            throw new Error(errorMessage);
        }

        const blob = await response.blob();
        let fileName = `case-${caseId}-calculonAllocationAdvice.pdf`;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
            let fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (!fileNameMatch) {
                fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            }
            if (!fileNameMatch) {
                fileNameMatch = contentDisposition.match(/filename\*=([^;]+)/);
            }
            if (fileNameMatch?.[1]) {
                fileName = fileNameMatch[1].replace(/^['"]|['"]$/g, '').trim();
                try {
                    fileName = decodeURIComponent(fileName);
                } catch {
                    // keep as-is
                }
            }
        }
        return { blob, fileName };
    }

    /**
     * Exports a case as a ZIP (JSON + all worksheet PDFs). Root only; single server-side API.
     * @param caseId - The case ID
     * @returns Promise containing the ZIP blob and filename
     */
    async exportCaseZip(caseId: string): Promise<{ blob: Blob; fileName: string }> {
        const response = await this.postJson('/v1/pdf/export-case-zip', { caseId });
        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
                // ignore
            }
            throw new Error(errorMessage);
        }
        const blob = await response.blob();
        let fileName = `case_${caseId}_export.zip`;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/) ?? contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            if (match?.[1]) {
                fileName = match[1].replace(/^['"]|['"]$/g, '').trim();
                try {
                    fileName = decodeURIComponent(fileName);
                } catch {
                    // keep as-is
                }
            }
        }
        return { blob, fileName };
    }

    /**
     * Downloads a PDF file to the user's device
     * @param blob - The PDF blob to download
     * @param fileName - The filename for the download
     * @returns The blob URL (kept alive for opening the file)
     */
    downloadPdf(blob: Blob, fileName: string): string {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Don't revoke the URL immediately - return it so it can be used to open the file
        // The caller should revoke it when done
        return url;
    }

    /**
     * Gets the URL for viewing a PDF in the browser
     * @param caseId - The case ID
     * @param pdfType - The type of PDF (e.g., "summary", "worksheet1")
     * @returns The URL to open in a new tab
     */
    getPdfViewUrl(caseId: string, pdfType: string): string {
        // First, check if it's a display name and convert to type
        const typeFromName = getPdfMapByName(pdfType);
        const internalType = typeFromName || pdfType;

        // Then map internal type to server enum value
        const serverPdfType = getPdfTypeToServerEnum(internalType);
        const url = this.buildUrl(`/v1/pdf/view?caseId=${encodeURIComponent(caseId)}&pdfType=${encodeURIComponent(serverPdfType)}`);
        return url;
    }

}

// Export a singleton instance
export const pdfApiService = new PdfApiService();

